<section class="d-flex flex-column gap-2" data-testid="ops-health-page">
  <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
    <div class="d-flex flex-column gap-1">
      <h2 class="av-section-title m-0">{{ 'backoffice.ops.health.title' | translate }}</h2>
      <p class="av-meta m-0">{{ 'backoffice.ops.health.subtitle' | translate }}</p>
    </div>

    <button type="button" class="av-btn" (click)="reload()" data-testid="ops-reload">
      <mat-icon aria-hidden="true">refresh</mat-icon>
      <span>{{ 'backoffice.common.reload' | translate }}</span>
    </button>
  </div>

  <p class="av-meta m-0" data-testid="ops-scope-note">{{ 'backoffice.ops.health.scope_note' | translate }}</p>
</section>

@switch (facade.healthState()) {
  @case ('error') {
    <av-empty-state
      class="mt-3"
      icon="error_outline"
      [title]="'backoffice.ops.health.error_title' | translate"
      [message]="'backoffice.ops.health.error_message' | translate"
    ></av-empty-state>
  }
  @case ('ready') {
    @if (facade.health(); as health) {
      <section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="ops-overall">
        <div class="d-flex flex-wrap align-items-center gap-3">
          <span class="av-meta">{{ 'backoffice.ops.health.overall' | translate }}</span>
          <av-status-chip
            [label]="healthLabelKey(health.overall) | translate"
            [tone]="healthTone(health.overall)"
          ></av-status-chip>

          <span class="av-meta">{{ 'backoffice.ops.health.queue_name' | translate }}</span>
          <span class="cell-strong av-source" data-testid="ops-queue-name">{{ health.queue_name }}</span>
        </div>

        <div class="d-flex flex-wrap gap-3">
          <div class="section-card av-card p-3 d-flex flex-column gap-2" data-testid="section-queue">
            <div class="d-flex align-items-center justify-content-between gap-2">
              <span class="cell-strong">{{ 'backoffice.ops.health.queue.title' | translate }}</span>
              <av-status-chip
                [label]="healthLabelKey(health.queue.status) | translate"
                [tone]="healthTone(health.queue.status)"
              ></av-status-chip>
            </div>

            <span class="av-meta">{{
              'backoffice.ops.health.queue.failed' | translate: { count: health.queue.failed_count }
            }}</span>
            <span class="av-meta">{{
              'backoffice.ops.health.queue.pending' | translate: { count: health.queue.pending_count }
            }}</span>
            <span class="av-meta">{{
              'backoffice.ops.health.queue.oldest_failure'
                | translate
                  : {
                      value:
                        health.queue.oldest_failed_at
                          ? (health.queue.oldest_failed_at | date: dateTimeFormat)
                          : unresolved,
                    }
            }}</span>
          </div>

          <div class="section-card av-card p-3 d-flex flex-column gap-2" data-testid="section-webhooks">
            <div class="d-flex align-items-center justify-content-between gap-2">
              <span class="cell-strong">{{ 'backoffice.ops.health.webhooks.title' | translate }}</span>
              <av-status-chip
                [label]="healthLabelKey(health.webhooks.status) | translate"
                [tone]="healthTone(health.webhooks.status)"
              ></av-status-chip>
            </div>

            <span class="av-meta">{{
              'backoffice.ops.health.webhooks.unprocessed'
                | translate: { count: health.webhooks.unprocessed_count }
            }}</span>
            <span class="av-meta">{{
              'backoffice.ops.health.webhooks.failed'
                | translate: { count: health.webhooks.failed_count }
            }}</span>
            <span class="av-meta">{{
              'backoffice.ops.health.webhooks.foreign'
                | translate: { count: health.webhooks.foreign_count }
            }}</span>
            <span class="av-meta">{{
              'backoffice.ops.health.webhooks.oldest_age'
                | translate: { value: health.webhooks.oldest_unprocessed_age_minutes ?? unresolved }
            }}</span>
          </div>

          <div
            class="section-card av-card p-3 d-flex flex-column gap-2"
            data-testid="section-reconciliation"
          >
            <div class="d-flex align-items-center justify-content-between gap-2">
              <span class="cell-strong">{{ 'backoffice.ops.health.reconciliation.title' | translate }}</span>
              <av-status-chip
                [label]="healthLabelKey(health.reconciliation.status) | translate"
                [tone]="healthTone(health.reconciliation.status)"
              ></av-status-chip>
            </div>

            <span class="av-meta">{{
              'backoffice.ops.health.reconciliation.last_run'
                | translate
                  : {
                      value:
                        health.reconciliation.last_run_at
                          ? (health.reconciliation.last_run_at | date: dateTimeFormat)
                          : unresolved,
                    }
            }}</span>
            <span class="av-meta">{{
              'backoffice.ops.health.reconciliation.checked'
                | translate: { value: health.reconciliation.checked_orgs ?? unresolved }
            }}</span>
            <span class="av-meta">{{
              'backoffice.ops.health.reconciliation.drifted'
                | translate: { value: health.reconciliation.drifted_orgs ?? unresolved }
            }}</span>
            <span class="av-meta">{{
              'backoffice.ops.health.reconciliation.critical'
                | translate: { value: health.reconciliation.critical_orgs ?? unresolved }
            }}</span>
          </div>

          <div class="section-card av-card p-3 d-flex flex-column gap-2" data-testid="section-stripe">
            <div class="d-flex align-items-center justify-content-between gap-2">
              <span class="cell-strong">{{ 'backoffice.ops.health.stripe.title' | translate }}</span>
              <av-status-chip
                [label]="healthLabelKey(health.stripe_api_version.status) | translate"
                [tone]="healthTone(health.stripe_api_version.status)"
              ></av-status-chip>
            </div>

            <span class="av-meta">{{
              'backoffice.ops.health.stripe.configured'
                | translate: { value: health.stripe_api_version.configured_api_version }
            }}</span>
            <span class="av-meta">{{
              'backoffice.ops.health.stripe.sdk'
                | translate: { value: health.stripe_api_version.sdk_api_version }
            }}</span>
          </div>
        </div>
      </section>

      <section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="section-scheduler">
        <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div class="d-flex flex-column gap-1">
            <h3 class="av-section-title m-0">{{ 'backoffice.ops.health.scheduler.title' | translate }}</h3>
            <p class="av-meta m-0">{{ 'backoffice.ops.health.scheduler.hint' | translate }}</p>
          </div>

          <av-status-chip
            [label]="healthLabelKey(health.scheduler.status) | translate"
            [tone]="healthTone(health.scheduler.status)"
          ></av-status-chip>
        </div>

        <p class="av-meta m-0" data-testid="scheduler-thresholds">
          {{
            'backoffice.ops.health.scheduler.thresholds'
              | translate
                : {
                    amber: health.scheduler.amber_after_minutes,
                    red: health.scheduler.red_after_minutes,
                  }
          }}
        </p>

        <div class="table-scroll">
          <table class="av-table w-100" data-testid="scheduler-table">
            <thead>
              <tr>
                <th scope="col">{{ 'backoffice.ops.health.scheduler.table.command' | translate }}</th>
                <th scope="col">{{ 'backoffice.ops.health.scheduler.table.cadence' | translate }}</th>
                <th scope="col">{{ 'backoffice.ops.health.scheduler.table.verdict' | translate }}</th>
                <th scope="col">{{ 'backoffice.ops.health.scheduler.table.last_run' | translate }}</th>
                <th scope="col">{{ 'backoffice.ops.health.scheduler.table.last_status' | translate }}</th>
                <th scope="col" class="text-end">{{ 'backoffice.ops.health.scheduler.table.age' | translate }}</th>
                <th scope="col" class="text-end">{{ 'backoffice.ops.health.scheduler.table.duration' | translate }}</th>
                <th scope="col">{{ 'backoffice.ops.health.scheduler.table.message' | translate }}</th>
              </tr>
            </thead>

            <tbody>
              @for (command of health.scheduler.commands; track command.command) {
                <tr>
                  <td class="cell-strong av-source" data-testid="scheduler-command">
                    {{ command.command }}
                  </td>
                  <td class="cell-muted">{{ cadenceFor(command.command) | translate }}</td>
                  <td>
                    <av-status-chip
                      [label]="healthLabelKey(command.status) | translate"
                      [tone]="healthTone(command.status)"
                    ></av-status-chip>
                  </td>
                  <td class="cell-muted" data-testid="scheduler-last-run">
                    {{
                      command.last_run_at
                        ? (command.last_run_at | date: dateTimeFormat)
                        : ('backoffice.ops.health.scheduler.never_run' | translate)
                    }}
                  </td>
                  <td class="cell-muted">{{ command.last_status ?? unresolved }}</td>
                  <td class="text-end">{{ command.age_minutes ?? unresolved }}</td>
                  <td class="text-end">{{ command.last_duration_ms ?? unresolved }}</td>
                  <td class="cell-muted">{{ command.last_message ?? unresolved }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <p class="av-meta m-0" data-testid="scheduler-command-count">
          {{ 'backoffice.ops.health.scheduler.command_count' | translate: { count: commandCount() } }}
        </p>

        <p class="av-meta m-0" data-testid="scheduler-not-deployed">{{ 'backoffice.ops.health.scheduler.not_deployed_note' | translate }}</p>
      </section>
    }
  }
  @default {
    <av-empty-state
      class="mt-3"
      icon="hourglass_empty"
      [title]="'backoffice.ops.health.loading_title' | translate"
      [message]="'backoffice.ops.health.loading_message' | translate"
    ></av-empty-state>
  }
}

<section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="failed-jobs">
  <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
    <div class="d-flex flex-column gap-1">
      <h3 class="av-section-title m-0">{{ 'backoffice.ops.failed_jobs.title' | translate }}</h3>
      <p class="av-meta m-0">{{ 'backoffice.ops.failed_jobs.hint' | translate }}</p>
    </div>

    <button
      type="button"
      class="av-btn"
      [disabled]="!facade.canRetry() || facade.failedJobs().length === 0"
      (click)="openRetryAll()"
      data-testid="retry-all"
    >
      {{ 'backoffice.ops.failed_jobs.retry_all' | translate }}
    </button>
  </div>

  @if (!facade.canRetry()) {
    <p class="av-meta m-0" data-testid="failed-jobs-restricted">{{ 'backoffice.ops.failed_jobs.restricted' | translate }}</p>
  }

  @switch (facade.failedJobsState()) {
    @case ('error') {
      <p class="av-meta m-0" data-testid="failed-jobs-error">{{ 'backoffice.ops.failed_jobs.error' | translate }}</p>
    }
    @case ('ready') {
      @if (facade.failedJobs().length === 0) {
        <p class="av-meta m-0" data-testid="failed-jobs-empty">{{ 'backoffice.ops.failed_jobs.empty' | translate }}</p>
      } @else {
        <div class="table-scroll">
          <table class="av-table w-100" data-testid="failed-job-table">
            <thead>
              <tr>
                <th scope="col">{{ 'backoffice.ops.failed_jobs.table.job' | translate }}</th>
                <th scope="col">{{ 'backoffice.ops.failed_jobs.table.queue' | translate }}</th>
                <th scope="col">{{ 'backoffice.ops.failed_jobs.table.failed_at' | translate }}</th>
                <th scope="col">{{ 'backoffice.ops.failed_jobs.table.exception' | translate }}</th>
                <th scope="col"></th>
              </tr>
            </thead>

            <tbody>
              @for (job of facade.failedJobs(); track job.uuid) {
                <tr>
                  <td class="av-source">{{ job.uuid }}</td>
                  <td class="cell-muted">{{ job.queue ?? unresolved }}</td>
                  <td class="cell-muted">
                    {{ job.failed_at ? (job.failed_at | date: dateTimeFormat) : unresolved }}
                  </td>
                  <td class="cell-muted">{{ job.exception ?? unresolved }}</td>
                  <td>
                    <button
                      type="button"
                      class="av-btn"
                      [disabled]="!facade.canRetry()"
                      (click)="openRetry(job.uuid)"
                      data-testid="retry-one"
                    >
                      {{ 'backoffice.ops.failed_jobs.retry_one' | translate }}
                    </button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    }
    @default {
      <p class="av-meta m-0">{{ 'backoffice.ops.failed_jobs.loading' | translate }}</p>
    }
  }
</section>
